// src/components/defined/specification_builder/SpecificationBuilderActionPanel.jsx

import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";
import Button from "../../reusable/Forms/Button";
import PinRepere from "../../reusable/Forms/PinRepere";
import RadioGroup from "../../reusable/Forms/RadioGroup";

/* ============================================================================
   SPECIFICATION BUILDER — ZONE C2
   ----------------------------------------------------------------------------
   RÔLE
   - recevoir les besoins entrants depuis Analysis
   - afficher un bac à sable léger et stable visuellement
   - permettre de sélectionner un besoin actif
   - afficher une mini-carte d’orientation pour guider la recherche dans C1
   - afficher un premier bloc de matching besoin / spécification
============================================================================ */

const T = {
  bg: theme?.colors?.background?.surface || "#ffffff",
  card: theme?.colors?.background?.Card || "#f9f9fa",
  panel: theme?.colors?.background?.panel || "#f1f3f6",

  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.darkblue || "#b2c1c7",
  borderPanel: theme?.colors?.borders?.panel || "#cfd8dd",

  text: theme?.colors?.text?.primary || "#164D63",
  body: theme?.colors?.text?.body || "#3F5D6E",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  green: theme?.colors?.text?.green || "#1A8C74",
  orange: theme?.colors?.text?.secondary || "#B8621B",

  blueBg: theme?.Generic?.blue?.pastel?.bg || "#E8F2F6",
  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  blueBorder: theme?.Generic?.blue?.pastel?.border || "#B7C6CC",

  greenBg: theme?.Generic?.green?.pastel?.bg || "#E7F3ED",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  greenBorder: theme?.Generic?.green?.pastel?.border || "#B9CEC4",

  yellowBg: theme?.Generic?.yellow?.pastel?.bg || "#F9F3E3",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  yellowBorder: theme?.Generic?.yellow?.pastel?.border || "#D8C6A6",

  greyBg: theme?.Generic?.grey?.pastel?.bg || "#EDF1F4",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  greyBorder: theme?.Generic?.grey?.pastel?.border || "#C7D0D6",
};

/* ============================================================================
   DONNÉES MOCK
   ----------------------------------------------------------------------------
   À remplacer plus tard par les besoins réellement envoyés depuis Analysis
   et par la spécification réellement sélectionnée dans C1.
============================================================================ */

const DEFAULT_PANEL_DATA = {
  incomingNeeds: [
    {
      id: "AN-024",
      shortText: "Définir la pression minimale au point défavorisé",
      system: "Pompage",
      subsystem: "Pompes de service",
      discipline: "Hydraulique",
      needType: "Nouvelle exigence",
      status: "À traiter",
    },
    {
      id: "AN-025",
      shortText: "Vérifier la continuité de service en cas de perte réseau",
      system: "Automatisme",
      subsystem: "Secours énergie",
      discipline: "Électricité",
      needType: "Adaptation",
      status: "En cours",
    },
    {
      id: "AN-026",
      shortText: "Préciser l’autonomie minimale des automatismes secourus",
      system: "Automatisme",
      subsystem: "Alimentation secourue",
      discipline: "Automatisme",
      needType: "Vérification",
      status: "Lié",
    },
  ],

  selectedArticle: {
    code: "HYD-01",
    title: "Pression minimale au point défavorisé",
    kind: "Performance",
    system: "Pompage",
    systems: ["Pompage"],
    disciplines: ["Hydraulique"],
  },
};

/* ============================================================================
   UI ATOMS
============================================================================ */

const SectionTitle = ({ children, compact = false }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: compact ? "0.76rem" : "0.80rem",
      fontWeight: theme?.fontWeights?.medium || 500,
      lineHeight: 1.2,
      color: T.soft,
      marginBottom: compact ? 6 : 8,
    }}
  >
    {children}
  </div>
);

const Divider = ({ light = false }) => (
  <div
    style={{
      width: "100%",
      height: 1,
      background: T.borderPanel,
      opacity: light ? 0.35 : 0.55,
    }}
  />
);

const MicroTag = ({ label, bg, text, border }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      minHeight: 24,
      padding: "0 8px",
      borderRadius: theme?.borders?.radius?.full || 9999,
      background: bg,
      color: text,
      border: `1px solid ${border || "transparent"}`,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.68rem",
      lineHeight: 1,
      fontWeight: 500,
      whiteSpace: "nowrap",
      boxSizing: "border-box",
    }}
  >
    {label}
  </div>
);

const MetaRow = ({ label, value }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "110px minmax(0, 1fr)",
      gap: 10,
      alignItems: "start",
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.70rem",
        lineHeight: 1.2,
        color: T.soft,
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.76rem",
        lineHeight: 1.3,
        color: T.body,
      }}
    >
      {value || "—"}
    </div>
  </div>
);

const EmptyState = () => (
  <div
    style={{
      width: "100%",
      minHeight: 220,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: 20,
      border: `1px solid ${T.border}`,
      background: T.card,
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        maxWidth: 240,
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.82rem",
        lineHeight: 1.45,
        color: T.soft,
      }}
    >
      Aucun besoin entrant n’est disponible pour le moment.
    </div>
  </div>
);

/* ============================================================================
   HELPERS
============================================================================ */

const getNeedStatusStyle = (value) => {
  const v = String(value || "").toLowerCase();

  if (v === "lié") {
    return {
      bg: T.greenBg,
      text: T.greenText,
      border: T.greenBorder,
    };
  }

  if (v === "en cours") {
    return {
      bg: T.blueBg,
      text: T.blueText,
      border: T.blueBorder,
    };
  }

  return {
    bg: T.yellowBg,
    text: T.yellowText,
    border: T.yellowBorder,
  };
};

const getMatchState = (activeNeed, selectedArticle) => {
  if (!activeNeed || !selectedArticle) {
    return {
      label: "Aucune spec liée",
      bg: T.greyBg,
      text: T.greyText,
      border: T.greyBorder,
    };
  }

  const sameSystem =
    String(activeNeed.system || "").toLowerCase() ===
    String(
      selectedArticle.system || selectedArticle.systems?.[0] || ""
    ).toLowerCase();

  const sameDiscipline =
    String(activeNeed.discipline || "").toLowerCase() ===
    String(selectedArticle.disciplines?.[0] || "").toLowerCase();

  if (sameSystem && sameDiscipline) {
    return {
      label: "Compatible",
      bg: T.greenBg,
      text: T.greenText,
      border: T.greenBorder,
    };
  }

  if (sameSystem || sameDiscipline) {
    return {
      label: "Partielle",
      bg: T.yellowBg,
      text: T.yellowText,
      border: T.yellowBorder,
    };
  }

  return {
    label: "Non adaptée",
    bg: T.blueBg,
    text: T.blueText,
    border: T.blueBorder,
  };
};

/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationBuilderActionPanel = ({
  panelData = DEFAULT_PANEL_DATA,
}) => {
  const incomingNeeds = panelData?.incomingNeeds || [];
  const selectedArticle = panelData?.selectedArticle || null;

  const [activeNeedId, setActiveNeedId] = useState(
    incomingNeeds?.[0]?.id || null
  );

  const activeNeed = useMemo(() => {
    return (
      incomingNeeds.find((item) => item.id === activeNeedId) ||
      incomingNeeds[0] ||
      null
    );
  }, [incomingNeeds, activeNeedId]);

    const isLinked =
  activeNeed &&
  selectedArticle &&
  activeNeed.linkedSpec === selectedArticle.code;


// ============================================================
// STATE — COMPLETENESS (à ajouter dans ton composant)
// ============================================================
const [completeness, setCompleteness] = useState("NON_EVALUE");
  const matchState = getMatchState(activeNeed, selectedArticle);

  if (!incomingNeeds.length) {
    return <EmptyState />;
  }

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        padding: 6,
        boxSizing: "border-box",
      }}
    >

      
      {/* =====================================================================
         1. BAC À SABLE — BESOINS ENTRANTS
         ===================================================================== */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "0.95rem",
            lineHeight: 1.1,
            fontWeight: theme?.fontWeights?.medium || 500,
            color: theme.colors.text.caneva,
            marginBottom: 8,
          }}
        >
          Besoins entrants
        </div>

        <div
          style={{
            background: "#ffffff",
            border: `1px solid ${T.borderStrong}`,
            borderRadius: theme?.borders?.radius?.xs || 4,
            borderLeft: `1px solid ${T.borderStrong}`,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            padding: 8,
            minHeight: 240,
            maxHeight: 240,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {incomingNeeds.map((need, index) => {
            const isActive = activeNeed?.id === need.id;
            const statusStyle = getNeedStatusStyle(need.status);

            return (
<button
  key={need.id}
  type="button"
  onClick={() => setActiveNeedId(need.id)}
  onMouseEnter={(e) => {
    if (!isActive) {
      e.currentTarget.style.background = "rgba(184, 98, 27, 0.02)";
    }
  }}
  onMouseLeave={(e) => {
    if (!isActive) {
      e.currentTarget.style.background = "#ffffff";
    }
  }}
  style={{
    position: "relative",
    overflow: "visible",

    width: "100%",
    border: "none",
    background: "#ffffff",
    boxShadow: isActive ? theme.shadows.sm : "none",
    borderLeft: "3px solid transparent",
    borderRadius: 3,
    borderBottom:
      index === incomingNeeds.length - 1
        ? "none"
        : `1px solid ${T.border}`,
    padding: "8px 10px",
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
    boxSizing: "border-box",
  }}
>
  {isActive && (
    <PinRepere
      letter="B"
      variant="pastelOrange"
      size={24}
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 2,
        backgroundColor: "#ffffff",
        border: `2px solid ${T.orange}`,
        color: T.orange,
      }}
    />
  )}

  <div
    style={{
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      paddingLeft: isActive ? 26 : 0,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.69rem",
        lineHeight: 1.1,
        color: T.verySoft,
      }}
    >
      {need.id}
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.77rem",
        lineHeight: 1.28,
        color: T.text,
        fontWeight: isActive ? 500 : 400,
      }}
    >
      {need.shortText}
    </div>
  </div>

  <MicroTag
    label={need.status || "—"}
    bg={statusStyle.bg}
    text={statusStyle.text}
    border={statusStyle.border}
  />
</button>
            );
          })}
        </div>
      </div>

     
<div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: 10,
  }}
>
  <SectionTitle>Matching besoin / spécification</SectionTitle>

  {/* LIGNE MATCHING */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: 10,
    alignItems: "center",
  }}
>
  {/* BESOIN */}
  <div
    style={{
      minWidth: 0,
      display: "grid",
      gridTemplateColumns: "24px minmax(0, 1fr)",
      gap: 8,
      alignItems: "start",
    }}
  >
    <PinRepere
      letter="B"
      variant="pastelOrange"
      size={22}
      style={{
        backgroundColor: "#ffffff",
        border: `2px solid ${T.orange}`,
        color: T.orange,
        marginTop: 1,
      }}
    />

    <div
      style={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.68rem",
          lineHeight: 1.1,
          color: T.verySoft,
          textTransform: "uppercase",
        }}
      >
        Besoin actif
      </div>

      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.76rem",
          lineHeight: 1.28,
          color: T.text,
          fontWeight: 500,
        }}
      >
        {activeNeed?.id || "—"} — {activeNeed?.shortText || "—"}
      </div>
    </div>
  </div>

  {/* FLECHE */}
  <div
  style={{
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1rem",          
    fontWeight: 600,             
    lineHeight: 1,
    color: theme.colors.text.caneva, 
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  →
</div>

  {/* SPEC */}
  <div
    style={{
      minWidth: 0,
      display: "grid",
      gridTemplateColumns: "24px minmax(0, 1fr)",
      gap: 8,
      alignItems: "start",
    }}
  >
    <PinRepere
      letter="S"
      variant="pastelOrange"
      size={22}
      style={{
        backgroundColor: T.orange,
        border: "none",
        color: "#ffffff",
        marginTop: 1,
      }}
    />

    <div
      style={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.68rem",
          lineHeight: 1.1,
          color: T.verySoft,
          textTransform: "uppercase",
        }}
      >
        Spécification sélectionnée
      </div>

      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.76rem",
          lineHeight: 1.28,
          color: T.text,
          fontWeight: 500,
        }}
      >
        {selectedArticle?.code || "—"} —{" "}
        {selectedArticle?.title || "Aucune spécification sélectionnée"}
      </div>
    </div>
  </div>
</div>

  
</div>
{/* ============================================================
   LIGNE ACTION — CONNECT / DISCONNECT
   ------------------------------------------------------------
   OBJECTIF :
   - Fournir une action rapide de liaison / déliaison
   - Structure en 3 zones : gauche / centre / droite
   - Lecture horizontale équilibrée et stable
============================================================ */}

{/* ============================================================
   CONTENEUR PRINCIPAL — GRID 3 COLONNES
   ------------------------------------------------------------
   - 1fr / auto / 1fr
   - centre fixe, côtés flexibles
============================================================ */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    marginTop: 10,
    width: "100%",
  }}
>

  {/* ============================================================
     ZONE GAUCHE — ACTION CONNECTER
     ------------------------------------------------------------
     - Alignement gauche
     - Désactivé si déjà lié
  ============================================================ */}
  <div
    style={{
      justifySelf: "start",
      display: "flex",
      width: "100%",
    }}
  >
    <Button variant="pastelGreen" disabled={isLinked}>
      CONNECTER
    </Button>
  </div>

  {/* ============================================================
     ZONE CENTRE — INDICATEUR DE LIEN
     ------------------------------------------------------------
     - Position centrale fixe
     - Icône 🔗
     - Couleur + opacité dynamiques
  ============================================================ */}
  <div
    style={{
      justifySelf: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 24,
    }}
  >
    <div
      style={{
        fontSize: "0.9rem",
        color: isLinked ? T.green : T.verySoft,
        opacity: isLinked ? 1 : 0.4,
      }}
    >
      🔗
    </div>
  </div>

  {/* ============================================================
     ZONE DROITE — ACTION DECONNECTER
     ------------------------------------------------------------
     - Alignement droite
     - Désactivé si non lié
  ============================================================ */}
  <div
    style={{
      justifySelf: "end",
      display: "flex-start",
    }}
  >
    <Button variant="pastelRed" disabled={!isLinked}>
      DECONNECTER
    </Button>
  </div>
  

</div>
<RadioGroup
  label="Complétude"
  name="completeness"
  value={completeness}
  onChange={setCompleteness}
  orientation="horizontal"
  labelWidth={90}
  compact={true}
  width="100%"
  justify="space-between"
  colorMap={theme.completenessLevels}
  style={{ gap: 24 }} // 🔥 AUGMENTE L’ESPACE LABEL ↔ CHAMPS
  options={[
    { value: "NON_EVALUE", label: "Non évalué" },
    { value: "PARTIEL", label: "Partiel" },
    { value: "COMPLET", label: "Complet" },
  ]}
/>

{/* ============================================================
   FIN DU BLOC ACTION
============================================================ */}

    </div>
  );
};

export default SpecificationBuilderActionPanel;